import React, { useMemo } from 'react';
import { Box, Divider, useTheme } from '@mui/material';
import {
  TagFilterV2Component,
  pluralize,
  DishSummary,
  Typography,
} from '@fdha/web-ui-library';
import {
  MealkitVariant,
  MealOptionType,
  MenuOptionSelectionAvailable,
} from '@fdha/graphql-api-admin';
import { SectionType } from '@fdha/common-utils';
import { TagFilterOption } from '@fdha/common-hooks';

interface ChooseMealsMenuSectionProps {
  title: string;
  sectionType: SectionType;
  selectedCount: number;
  requiredCount: number;
  subSectionSubTitle?: {
    prepared: string;
    mealKit: string;
  };
  filterState: {
    filterOptions: TagFilterOption[];
    appliedFilters: string[];
    draftResults: MenuOptionSelectionAvailable[];
    results: MenuOptionSelectionAvailable[];
    handleApply: (selectedValues: string[]) => void;
    handleCancel: () => void;
    handleDraftChange: (selectedValues: string[]) => void;
  };
  onItemClick: (
    item: MenuOptionSelectionAvailable,
    sectionType: SectionType
  ) => void;
}

export const ChooseMealsMenuSection: React.FC<ChooseMealsMenuSectionProps> = ({
  title,
  sectionType,
  selectedCount,
  requiredCount,
  subSectionSubTitle,
  filterState,
  onItemClick,
}) => {
  const theme = useTheme();

  const getTags = (item: MenuOptionSelectionAvailable) => {
    const tags = item.tagsByCategory?.map(({ tags }) =>
      tags.map(({ name }) => name)
    );
    return tags?.flat();
  };

  // Extract filter state from props
  const {
    filterOptions,
    appliedFilters,
    draftResults,
    results: filteredItems,
    handleApply,
    handleCancel,
    handleDraftChange,
  } = filterState;

  const filteredPreparedMeals = filteredItems.filter(
    (item) => item.mealOptionType === MealOptionType.Dish
  );

  const filteredMealKits = filteredItems.filter(
    (item) => item.mealOptionType === MealOptionType.MealKit
  );

  // Filter labels
  const filterButtonConfirmLabel = useMemo(() => {
    const resultCount = draftResults.length;
    return `Show ${resultCount > 0 ? resultCount : 'All'} ${pluralize(
      resultCount,
      'Meal',
      undefined,
      true
    )}`;
  }, [draftResults.length]);

  const handleItemClick = (item: MenuOptionSelectionAvailable) => {
    onItemClick(item, sectionType);
  };

  const getSelectedVariants = (mealkitVariants: MealkitVariant[]) => {
    if (mealkitVariants.length === 1) return [];

    return mealkitVariants
      ?.filter((variant) => variant.quantity > 0)
      .map((variant) => ({
        name: variant.ingredientName,
        quantity: variant.quantity + 'x',
      }));
  };

  const renderFilter = () => {
    if (!filterOptions.length) return null;

    return (
      <Box sx={{ mb: 2 }}>
        <TagFilterV2Component
          key={`filter-${sectionType}`}
          anchorTitle={'Filters'}
          containerTitle={'Filters'}
          options={filterOptions}
          appliedValue={appliedFilters}
          confirmButtonLabel={filterButtonConfirmLabel}
          clearButtonLabel={'Clear all'}
          onDraftChange={handleDraftChange}
          onApply={handleApply}
          onCancel={handleCancel}
          results={filteredItems}
        />
      </Box>
    );
  };

  const renderItems = (
    itemList: MenuOptionSelectionAvailable[],
    subtitle?: string,
    subTitleDescription?: string
  ) => {
    if (itemList.length === 0) return null;
    return (
      <Box sx={{ mb: 2 }}>
        <Box padding={2} bgcolor={theme.palette.info.lightBg}>
          <>
            <Typography variant="h6" fontWeight="bold">
              {subtitle}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {subTitleDescription}
            </Typography>
          </>
        </Box>
        <Box display="flex" flexDirection="column" padding={2}>
          {itemList.map((item, index) => (
            <Box key={`${item.menuOptionId}`}>
              <DishSummary
                image={item?.photoUrl || ''}
                title={item.name}
                tags={getTags(item)}
                quantity={item.quantity || 0}
                onViewDetails={() => handleItemClick(item)}
                selectedVariants={getSelectedVariants(
                  item.mealkitVariants || []
                )}
              />
              {index !== itemList.length - 1 && (
                <Divider sx={{ height: '1px', my: 2 }} />
              )}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Box display="flex" flexDirection="column" padding={2} rowGap={2}>
        <Box component="div">
          <Typography variant="h6" color="text.primary">
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You have selected{' '}
            <strong>
              {selectedCount} out of {requiredCount}
            </strong>{' '}
            {sectionType === 'entreeDishes' ? 'entree dishes' : 'snacks/sides'}
          </Typography>
        </Box>
        {renderFilter()}
      </Box>

      {sectionType === 'entreeDishes' ? (
        <>
          {renderItems(
            filteredPreparedMeals,
            'Prepared',
            subSectionSubTitle?.prepared
          )}
          {renderItems(
            filteredMealKits,
            'Meal Kit',
            subSectionSubTitle?.mealKit
          )}
        </>
      ) : (
        renderItems(filteredItems, 'Prepared', subSectionSubTitle?.prepared)
      )}
    </Box>
  );
};
